// apps/frontend/src/hooks/useAuth.tsx
// 🎯 Hook Auth Premium Enterprise - Marketplace optimisé

import {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import apiClient from '../services/apiClient';
import { analytics } from '../lib/analytics';

// ============================================================================
// 🎯 TYPES & INTERFACES
// ============================================================================

export interface AuthUser {
  id: string;
  email: string;
  username?: string;
  firstName: string;
  lastName: string;
  role: string;
  isVerified: boolean;
  preferredLocale: string;
  avatarUrl?: string;
  lastLoginAt?: string;
  createdAt?: string;
  // 🔥 Informations Premium
  subscriptionType?: 'FREE' | 'BASIC' | 'PRO' | 'PREMIUM';
  subscriptionStatus?: 'ACTIVE' | 'INACTIVE' | 'CANCELLED' | 'EXPIRED';
  canUseAI?: boolean;
  isPremium?: boolean;
  // Backend renvoie 3 valeurs : PARTICULIER (vide-dressing), PRO (vendeur
  // regulier, abonnement Pro outils possible) ou PROFESSIONNEL (KBIS
  // verifie). Le type frontend les expose toutes les 3 sinon le gate
  // trial sur PricingPage / autres surfaces casse en silence.
  sellerType?: 'PARTICULIER' | 'PRO' | 'PROFESSIONNEL';
  // 🌍 Localisation (pour marketplace)
  bio?: string;
  timezone?: string;
  countryCode?: string;
  city?: string;
  // Adresse expedition vendeur : remplie just-in-time avant la 1ere mise
  // en vente (cf. SellerAddressCaptureModal). Sans ces 3 champs, SendCloud
  // utilise l'adresse par defaut du compte plateforme — les colis partent
  // au nom de Glowp au lieu du nom du vendeur.
  streetAddress?: string;
  postalCode?: string;
  phone?: string;
  // RGPD : null = jamais demande ou refuse, ISO string = date du consentement
  acceptedMarketingAt?: string | null;
}

interface BackendStatus {
  connected: boolean;
  url: string;
  message: string;
  latency?: number;
  version?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  backendStatus: BackendStatus;
  tokenExpiresAt: number | null;
  sessionExpiresIn: number | null;
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; message?: string; code?: string }>;
  register: (
    data: RegisterData
  ) => Promise<{ success: boolean; message?: string }>;
  socialLogin: (
    provider: 'GOOGLE' | 'APPLE',
    accessToken: string
  ) => Promise<{ success: boolean; message?: string }>;
  logout: (clearData?: boolean) => Promise<void>;
  getProfile: () => Promise<void>;
  testBackend: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  isTokenExpiringSoon: () => boolean;
  getSessionTimeRemaining: () => number;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  preferredLocale?: string;
  username?: string;
  referralCode?: string;
  acceptMarketing?: boolean;
}

// ============================================================================
// 🏗️ CONTEXT SETUP
// ============================================================================

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ============================================================================
// 🔄 MAPPING FUNCTIONS
// ============================================================================

const mapUserFromBackend = (backendUser: any): AuthUser => {
  return {
    id: backendUser.id || backendUser.user_id,
    email: backendUser.email,
    username: backendUser.username || undefined,
    firstName: backendUser.first_name || backendUser.firstName || '',
    lastName: backendUser.last_name || backendUser.lastName || '',
    role: backendUser.role || 'USER',
    isVerified: backendUser.is_verified || backendUser.isVerified || false,
    preferredLocale:
      backendUser.preferred_locale || backendUser.preferredLocale || 'fr-FR',
    avatarUrl: backendUser.avatar_url || backendUser.avatarUrl,
    lastLoginAt: backendUser.last_login_at || backendUser.lastLoginAt,
    createdAt: backendUser.created_at || backendUser.createdAt,
    // 🔥 Informations Premium
    subscriptionType:
      backendUser.subscription_type || backendUser.subscriptionType || 'FREE',
    subscriptionStatus:
      backendUser.subscription_status ||
      backendUser.subscriptionStatus ||
      'INACTIVE',
    canUseAI: backendUser.can_use_ai || backendUser.canUseAI || false,
    isPremium: backendUser.is_premium || backendUser.isPremium || false,
    sellerType:
      backendUser.seller_type || backendUser.sellerType || 'PARTICULIER',
    // 🌍 Localisation
    bio: backendUser.bio,
    timezone: backendUser.timezone,
    countryCode: backendUser.country_code || backendUser.countryCode,
    city: backendUser.city,
    streetAddress:
      backendUser.street_address || backendUser.streetAddress || undefined,
    postalCode: backendUser.postal_code || backendUser.postalCode || undefined,
    phone: backendUser.phone || undefined,
    acceptedMarketingAt:
      backendUser.accepted_marketing_at ??
      backendUser.acceptedMarketingAt ??
      null,
  };
};

// ============================================================================
// 🎪 AUTH PROVIDER PREMIUM
// ============================================================================

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // 🎯 États principaux
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [backendStatus, setBackendStatus] = useState<BackendStatus>({
    connected: false,
    url: '',
    message: 'Vérification backend...',
  });
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);

  // 🔄 Refs pour éviter les memory leaks
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);
  const isMountedRef = useRef(true);

  // ============================================================================
  // 🧹 CLEANUP FUNCTION
  // ============================================================================

  const cleanup = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    isRefreshingRef.current = false;
  }, []);

  // 🧹 Cleanup au démontage
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // ============================================================================
  // ⏰ TOKEN MANAGEMENT FUNCTIONS
  // ============================================================================

  const extractTokenExpiration = useCallback(
    (token?: string): number | null => {
      try {
        const actualToken = token || localStorage.getItem('glowp_access_token');
        if (!actualToken) return null;

        const tokenPayload = JSON.parse(atob(actualToken.split('.')[1]));
        return tokenPayload.exp * 1000;
      } catch (error) {
        console.error('❌ Erreur décodage token:', error);
        return null;
      }
    },
    []
  );

  const isTokenExpiringSoon = useCallback((): boolean => {
    if (!tokenExpiresAt) return false;
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() + fiveMinutes >= tokenExpiresAt;
  }, [tokenExpiresAt]);

  const getSessionTimeRemaining = useCallback((): number => {
    if (!tokenExpiresAt) return 0;
    return Math.max(0, tokenExpiresAt - Date.now());
  }, [tokenExpiresAt]);

  // ============================================================================
  // 🔄 TOKEN REFRESH INTELLIGENT
  // ============================================================================

  const scheduleTokenRefresh = useCallback(() => {
    cleanup();

    if (!user || !isMountedRef.current) return;

    const token = localStorage.getItem('glowp_access_token');
    if (!token) return;

    const expirationTime = extractTokenExpiration(token);
    if (!expirationTime) return;

    setTokenExpiresAt(expirationTime);

    const now = Date.now();
    const timeUntilRefresh = expirationTime - now - 5 * 60 * 1000; // 5 min avant expiration

    if (timeUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(async () => {
        if (!isMountedRef.current || isRefreshingRef.current) return;

        isRefreshingRef.current = true;

        try {
          const refreshSuccess = await apiClient.refreshAccessToken();
          if (refreshSuccess && isMountedRef.current) {
            const newExpiration = extractTokenExpiration();
            setTokenExpiresAt(newExpiration);
            scheduleTokenRefresh();
          } else if (isMountedRef.current) {
            await logout(false);
          }
        } catch (error) {
          console.error('❌ Erreur refresh token:', error);
          if (isMountedRef.current) {
            await logout(false);
          }
        } finally {
          isRefreshingRef.current = false;
        }
      }, timeUntilRefresh);
    } else {
      refreshToken().catch(() => logout(false));
    }
  }, [user, extractTokenExpiration, cleanup]);

  const refreshToken = async (): Promise<boolean> => {
    if (isRefreshingRef.current) return false;

    isRefreshingRef.current = true;
    try {
      const success = await apiClient.refreshAccessToken();
      if (success) {
        const newExpiration = extractTokenExpiration();
        setTokenExpiresAt(newExpiration);
        scheduleTokenRefresh();
      }
      return success;
    } catch (error) {
      console.error('❌ Erreur refresh manuel:', error);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  };

  // ============================================================================
  // 💓 HEARTBEAT & BACKEND MONITORING
  // ============================================================================

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) return;

    heartbeatIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current) return;

      try {
        const status = await apiClient.testConnection();
        if (isMountedRef.current) {
          setBackendStatus(_prevStatus => ({
            ...status,
            message: status.connected
              ? 'Backend connecté'
              : 'Backend déconnecté',
          }));
        }
      } catch {
        if (isMountedRef.current) {
          setBackendStatus(prevStatus => ({
            ...prevStatus,
            connected: false,
            message: 'Backend déconnecté',
          }));
        }
      }
    }, 120_000); // 2 minutes (was 30s — caused rate limiting in prod)
  }, []);

  // ============================================================================
  // 🚀 INITIALIZATION
  // ============================================================================

  useEffect(() => {
    const initAuth = async () => {
      try {
        const status = await apiClient.testConnection();

        const newBackendStatus = {
          connected: status.connected,
          url: status.url,
          message: status.connected ? 'Backend connecté' : 'Backend déconnecté',
          latency: status.latency,
          version: status.version,
        };
        setBackendStatus(newBackendStatus);

        const hasToken = !!localStorage.getItem('glowp_access_token');
        if (status.connected && hasToken) {
          try {
            await getProfile();
            if (isMountedRef.current) {
              const expiration = extractTokenExpiration();
              setTokenExpiresAt(expiration);
              startHeartbeat();
            }
          } catch (error) {
            console.error('❌ Erreur getProfile:', error);
            apiClient.clearTokens();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [extractTokenExpiration, startHeartbeat]);

  // Sync Sentry user context : attache user.id aux erreurs suivantes,
  // null si logout. RGPD : juste id + role, pas d'email (cf. lib/sentry.ts).
  useEffect(() => {
    import('../lib/sentry').then(({ setSentryUser }) => {
      setSentryUser(user ? { id: user.id, role: user.role } : null);
    });
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (user && apiClient.isAuthenticated()) {
      scheduleTokenRefresh();
      startHeartbeat();
    } else {
      cleanup();
    }
  }, [user, scheduleTokenRefresh, startHeartbeat, cleanup]);

  // ============================================================================
  // 🔐 AUTH FUNCTIONS
  // ============================================================================

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await apiClient.login({ email, password });

      if (response.success && response.data?.user) {
        const userData = (response.data as any).user || response.data;
        const mappedUser = mapUserFromBackend(userData);
        if (isMountedRef.current) {
          setUser(mappedUser);
          const expiration = extractTokenExpiration();
          setTokenExpiresAt(expiration);
        }
        analytics.userLoggedIn('email');
        return { success: true };
      } else {
        return {
          success: false,
          message:
            response.error || response.message || 'Échec de la connexion',
          // Remonte le code metier du backend (ex: 'EMAIL_NOT_VERIFIED')
          // pour que la UI puisse offrir le bon recovery (bouton resend).
          code: (response as any).code,
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur de connexion',
        code: error.code,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setIsLoading(true);

      // Recuperer le code parrainage depuis localStorage (set par RegisterRedirectWrapper)
      const storedReferralCode = localStorage.getItem('glowp_referral_code');
      const referralCode = data.referralCode || storedReferralCode || undefined;

      // Attribution signup (source + campagne credits IA) capturee en
      // amont par les pages /famille, /moments, etc.
      const { getAttributionForSignup, clearSignupAttribution } =
        await import('../lib/signupAttribution');
      const attribution = getAttributionForSignup();

      const response = await apiClient.register({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        referralCode,
        acceptMarketing: data.acceptMarketing === true,
        ...(data.username ? { username: data.username } : {}),
        ...(attribution.signupSource
          ? { signupSource: attribution.signupSource }
          : {}),
        ...(attribution.campaign ? { campaign: attribution.campaign } : {}),
      });

      if (response.success && response.data) {
        // 🔥 AUTO-CONNEXION après inscription
        // apiClient.register a déjà appelé setTokens() : on récupère juste
        // l'utilisateur. Avant, on cherchait response.data.token (singulier)
        // qui n'existe pas — le backend renvoie data.tokens.{accessToken,
        // refreshToken}. Conséquence : setUser n'était jamais appelé,
        // isAuthenticated restait false, et les CTAs post-inscription
        // ("Personnaliser ma boutique", "Ajouter mon premier article")
        // n'ouvraient aucune modal — l'écran retombait sur la home.
        const userData = (response.data as any).user;

        if (userData) {
          const mappedUser = mapUserFromBackend(userData);
          if (isMountedRef.current) {
            setUser(mappedUser);
            const expiration = extractTokenExpiration();
            setTokenExpiresAt(expiration);
          }

          localStorage.removeItem('glowp_referral_code');
          clearSignupAttribution();
          analytics.userRegistered('email');
          // Si une campagne a ete consommee cote backend, on persiste le
          // bonus en localStorage (PAS sessionStorage) pour qu'il survive
          // au cycle de verification email : le user clique le lien dans
          // son mail, ce qui ouvre un nouvel onglet -> sessionStorage de
          // l'onglet d'inscription est PERDU et l'ecran 'tu as 100 credits
          // + bouton publier' n'apparait jamais. Confirme par l'audit du
          // 03/06/2026 : Manon et Beryl sont passees par /verify-email
          // et n'ont jamais vu la modale -> aucune publication.
          // TTL 7 jours : le user a une semaine pour revenir et voir
          // l'ecran. Au-dela, on considere le moment depasse.
          const bonus = (response.data as any).campaignBonus;
          if (bonus && typeof bonus.credits === 'number') {
            try {
              localStorage.setItem(
                'glowp_signup_bonus',
                JSON.stringify({
                  ...bonus,
                  capturedAt: Date.now(),
                })
              );
            } catch {
              /* localStorage indisponible -> tant pis */
            }
          }
          return { success: true, message: 'Compte créé avec succès !' };
        }

        clearSignupAttribution();
        analytics.userRegistered('email');
        return {
          success: true,
          message: response.data?.message || 'Inscription réussie !',
        };
      } else {
        return {
          success: false,
          message:
            response.error || response.message || "Échec de l'inscription",
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || "Erreur d'inscription",
      };
    } finally {
      setIsLoading(false);
    }
  };

  const socialLogin = async (
    provider: 'GOOGLE' | 'APPLE',
    accessToken: string
  ) => {
    try {
      setIsLoading(true);
      const response = await apiClient.socialLogin(provider, accessToken);

      if (response.success && response.data?.user) {
        const userData = (response.data as any).user || response.data;
        const isNewUser = (response.data as any).isNewUser === true;
        const mappedUser = mapUserFromBackend(userData);
        if (isMountedRef.current) {
          setUser(mappedUser);
          const expiration = extractTokenExpiration();
          setTokenExpiresAt(expiration);
        }
        const method = provider.toLowerCase() as 'google' | 'apple';
        if (isNewUser) {
          analytics.userRegistered(method);
        }
        analytics.userLoggedIn(method);
        return { success: true };
      } else {
        return {
          success: false,
          message:
            response.error ||
            response.message ||
            'Échec de la connexion sociale',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur de connexion',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (clearData: boolean = false) => {
    try {
      analytics.userLoggedOut();
      cleanup();

      if (clearData) {
        localStorage.clear();
      } else {
        localStorage.removeItem('glowp_access_token');
      }

      setUser(null);
      setTokenExpiresAt(null);

      await apiClient.logout();

      // 🔥 REDIRECTION VERS ACCUEIL
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/';
    }
  };

  const getProfile = async () => {
    try {
      const response = await apiClient.getProfile();

      if (response?.success && response?.data) {
        const userData = (response.data as any).user || response.data;
        const mappedUser = mapUserFromBackend(userData);
        setUser(mappedUser);
      }
    } catch (error) {
      console.error('❌ Failed to get profile:', error);
    }
  };

  const testBackend = async () => {
    const status = await apiClient.testConnection();
    if (isMountedRef.current) {
      setBackendStatus({
        ...status,
        message: status.connected ? 'Backend connecté' : 'Backend déconnecté',
      });
    }
  };

  // ============================================================================
  // 🎯 COMPUTED VALUES
  // ============================================================================

  const sessionExpiresIn = getSessionTimeRemaining();
  const isAuthenticated = !!user && apiClient.isAuthenticated();

  // ============================================================================
  // 🎪 PROVIDER RENDER
  // ============================================================================

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        backendStatus,
        tokenExpiresAt,
        sessionExpiresIn,
        login,
        register,
        socialLogin,
        logout,
        getProfile,
        testBackend,
        refreshToken,
        isTokenExpiringSoon,
        getSessionTimeRemaining,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
